import { creatorbase } from '@round/api';
import styles from './PlatformPost.module.css';
import { formatNumberToKNotation } from '@round/utils';
import { isNumber } from 'utility/utility';
import { Tooltip, assets } from '@round/whitelabel-ui';
import { numberWithCommas } from 'helpers';
import { MediaPlayEvent } from '@vidstack/react';
import { lazy, Suspense } from 'react';
import { Skeleton } from '@round/ui-kit';
import { TooltipOnOverflow } from '@round/whitelabel-ui';

const {
    PlayBold: PlayBoldIcon,
    LikeBold: LikeBoldIcon,
    BookmarkBold: BookmarkBoldIcon,
    SendBold: SendBoldIcon,
    CommentBold: CommentBoldIcon,
    User: UserIcon,
} = assets;

const PlatformPostPlayer = lazy(() => import('./PlatformPostPlayer/PlatformPostPlayer'));

export type PlatformPostData = {
    id: string;
    platform: creatorbase.Platform;
    countryCode: string | null;
    createdDate: string | null;
    audioId: number | null;
    audioTitle: string | null;
    audioAuthorName: string | null;
    audioIsOriginal: boolean | null;
    platformId: string | null;
    platformUrl: string | null;
    description: string | null;
    authorUsername: string | null;
    authorNickname: string | null;
    authorProfileUrl: string | null;
    coverImageUrl: string;
    videoPlayUrl: string | null;
    adCode: string;
    likeCount: number | null;
    commentCount: number | null;
    shareCount: number | null;
    viewCount: number | null;
    saveCount: number | null;
    followerCount: number | null;
};

type StatsKey = keyof Pick<PlatformPostData, 'viewCount' | 'likeCount' | 'commentCount' | 'shareCount' | 'saveCount'>;
const statsIcons: Record<StatsKey, typeof PlayBoldIcon> = {
    viewCount: PlayBoldIcon,
    likeCount: LikeBoldIcon,
    commentCount: CommentBoldIcon,
    shareCount: SendBoldIcon,
    saveCount: BookmarkBoldIcon,
};

type Props = {
    data: PlatformPostData | null | undefined;
    onPlay?: (event: MediaPlayEvent) => void;
};

const PlatformPost = ({ data, onPlay }: Props) => {
    const { viewCount, likeCount, commentCount, shareCount, saveCount } = data ?? {};
    const stats: Record<StatsKey, string> = {
        viewCount: isNumber(viewCount) && Number.isFinite(viewCount) ? formatNumberToKNotation(viewCount) : '-',
        likeCount: isNumber(likeCount) && Number.isFinite(likeCount) ? formatNumberToKNotation(likeCount) : '-',
        commentCount:
            isNumber(commentCount) && Number.isFinite(commentCount) ? formatNumberToKNotation(commentCount) : '-',
        shareCount: isNumber(shareCount) && Number.isFinite(shareCount) ? formatNumberToKNotation(shareCount) : '-',
        saveCount: isNumber(saveCount) && Number.isFinite(saveCount) ? formatNumberToKNotation(saveCount) : '',
    };

    return (
        <div className={styles.container}>
            <Suspense fallback={<Skeleton className={styles.playerSkeleton} />}>
                <PlatformPostPlayer data={data} onPlay={onPlay} />
            </Suspense>

            <div className={styles.stats}>
                {Object.entries(stats).map(([key, value]) => {
                    if (!value) {
                        return null;
                    }

                    const Icon = statsIcons[key as StatsKey];
                    const rawValue = data?.[key as StatsKey];
                    const hasNoValue = !isNumber(rawValue) || !Number.isFinite(rawValue);
                    const label = key.replace('Count', rawValue === 1 ? '' : 's');
                    const tooltip = hasNoValue ? `No ${label}` : `${numberWithCommas(rawValue)} ${label}`;

                    return (
                        <Tooltip.Root>
                            {(anchor, setAnchor) => (
                                <>
                                    <div ref={setAnchor} className={styles.statsEntry}>
                                        <Icon className={styles.statsIcon} />
                                        <span className={styles.statsValue}>{value}</span>
                                    </div>

                                    <Tooltip anchorElement={anchor}>
                                        <Tooltip.Body>
                                            <span>{tooltip}</span>
                                        </Tooltip.Body>
                                    </Tooltip>
                                </>
                            )}
                        </Tooltip.Root>
                    );
                })}
            </div>

            <div className={styles.profile}>
                <a
                    className={styles.profileUrl}
                    href={data?.authorProfileUrl ?? undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    @{data?.authorUsername}
                </a>

                {isNumber(data?.followerCount) && (
                    <Tooltip.Root>
                        {(anchor, setAnchor) => (
                            <>
                                <div ref={setAnchor} className={styles.statsEntry}>
                                    <UserIcon className={styles.statsIcon} />
                                    <span className={styles.statsValue}>
                                        {formatNumberToKNotation(data?.followerCount!)}
                                    </span>
                                </div>

                                <Tooltip anchorElement={anchor}>
                                    <Tooltip.Body>
                                        <span>{numberWithCommas(data?.followerCount!)}</span>
                                    </Tooltip.Body>
                                </Tooltip>
                            </>
                        )}
                    </Tooltip.Root>
                )}
            </div>

            <TooltipOnOverflow tooltip={data?.description} tooltipClassName={styles.descriptionTooltip}>
                <p className={styles.description}>{data?.description}</p>
            </TooltipOnOverflow>
        </div>
    );
};

const LoadingView = () => {
    return (
        <div className={styles.container}>
            <Skeleton className={styles.playerSkeleton} />
            <Skeleton width="75%" height="0.75rem" />

            <Skeleton width="7rem" height="0.75rem" />
            <Skeleton width="100%" height="0.75rem" />
        </div>
    );
};

PlatformPost.LoadingView = LoadingView;

export default PlatformPost;
